/**
 * `CompareChart` — overlay any two probes on one dual-Y-axis chart so
 * Josh can eyeball correlations (e.g. pH vs Alkalinity) over the
 * selected window. Controlled: the probe pair lives in the persisted
 * monitor config (`config.compare`) and changes flow up via `onChange`.
 */
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useHistoricalData } from '../hooks/useWebSocket';
import type { ComparePair } from './monitorConfig';

const DAY_MS = 24 * 60 * 60 * 1_000;

export interface CompareOption {
  /** Raw Apex probe name — also the timeseries sensorId + display label. */
  readonly id: string;
  readonly unit: string | null;
}

interface MergedRow {
  ts: number;
  a?: number;
  b?: number;
}

function mergeSeries(
  a: readonly { ts: number; value: number }[],
  b: readonly { ts: number; value: number }[],
): MergedRow[] {
  const map = new Map<number, MergedRow>();
  for (const p of a) {
    map.set(p.ts, { ts: p.ts, a: p.value });
  }
  for (const p of b) {
    const row = map.get(p.ts);
    if (row) row.b = p.value;
    else map.set(p.ts, { ts: p.ts, b: p.value });
  }
  return [...map.values()].sort((x, y) => x.ts - y.ts);
}

export function CompareChart({
  options,
  pair,
  onChange,
  fromMs,
  toMs,
}: {
  options: readonly CompareOption[];
  pair: ComparePair;
  onChange: (pair: ComparePair) => void;
  fromMs: number;
  toMs: number;
}) {
  const seriesA = useHistoricalData('apex', pair.a, fromMs, toMs, 300);
  const seriesB = useHistoricalData('apex', pair.b, fromMs, toMs, 300);

  const data = useMemo(
    () => mergeSeries(seriesA.points, seriesB.points),
    [seriesA.points, seriesB.points],
  );

  const windowMs = toMs - fromMs;
  const formatTs =
    windowMs <= 2 * DAY_MS
      ? (ts: number) =>
          new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : (ts: number) =>
          new Date(ts).toLocaleDateString([], { month: 'numeric', day: 'numeric' });

  if (options.length < 2) return null;

  const selectCls =
    'rounded-md border-reef-700 bg-reef-950 py-1 px-2 text-xs text-reef-200 focus:border-amber-500 focus:ring-amber-500';

  return (
    <section
      data-testid="compare-chart"
      className="rounded-xl border border-reef-800 bg-reef-900 p-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-reef-400">
          Compare
        </h3>
        <div className="flex items-center gap-2">
          <select
            aria-label="First probe"
            data-testid="compare-a"
            value={pair.a}
            onChange={(e) => onChange({ a: e.target.value, b: pair.b })}
            className={selectCls}
          >
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
          <span className="text-xs text-reef-500">vs</span>
          <select
            aria-label="Second probe"
            data-testid="compare-b"
            value={pair.b}
            onChange={(e) => onChange({ a: pair.a, b: e.target.value })}
            className={selectCls}
          >
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-[11px]">
        <span className="flex items-center gap-1.5 text-amber-300">
          <span className="inline-block h-0.5 w-4 bg-amber-400" /> {pair.a}
        </span>
        <span className="flex items-center gap-1.5 text-sky-300">
          <span className="inline-block h-0.5 w-4 bg-sky-400" /> {pair.b}
        </span>
      </div>

      <div className="mt-3 h-48 sm:h-56">
        {data.length < 2 ? (
          <div className="flex h-full items-center justify-center text-xs text-reef-500">
            Not enough history yet for this window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis
                dataKey="ts"
                type="number"
                domain={[fromMs, toMs]}
                tickFormatter={formatTs}
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
              />
              <YAxis
                yAxisId="left"
                domain={['auto', 'auto']}
                tick={{ fill: '#f59e0b', fontSize: 10 }}
                stroke="#334155"
                width={44}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={['auto', 'auto']}
                tick={{ fill: '#38bdf8', fontSize: 10 }}
                stroke="#334155"
                width={44}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#94a3b8' }}
                labelFormatter={(ts) => new Date(Number(ts)).toLocaleString()}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="a"
                name={pair.a}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="b"
                name={pair.b}
                stroke="#38bdf8"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
